import { socketLog, type FullSocketConfig } from '@features/websocket/socketService'
import { store } from '@store/store'

const WS_URL = process.env.NEXT_PUBLIC_WS_URL ?? '/'
export const NOTIFICATIONS_SOCKET_NAME = 'notifications'

export const socketConfigs: FullSocketConfig[] = [
  {
    name: NOTIFICATIONS_SOCKET_NAME,
    match: [/.*/],
    url: WS_URL,
    subscription: () => ({
      channel: 'Admin::NotificationsChannel',
      user_id: store.getState().session.user?.id,
    }),
  },

  {
    name: 'tasks',
    match: ['/tasks'],
    url: WS_URL,
    subscription: { channel: 'Admin::Tasks::IndexChannel' },
    onConnect: (subscription) => {
      try {
        subscription.perform?.('join_table', { table: 'tasks' })
      } catch (err) {
        console.error('subscription perform(join_table) for "tasks" failed', err)
      }

      return () => {
        try {
          subscription.perform?.('leave_table', { table: 'tasks' })
        } catch (err) {
          console.error('subscription perform(leave_table) for "tasks" failed', err)
        }
      }
    },
  },

  {
    name: 'tasks/:id',
    match: [/^\/tasks\/\d+$/],
    url: WS_URL,
    subscription: (path: string) => {
      const match = path.match(/^\/tasks\/(\d+)/)
      if (!match) {
        console.warn(`No match found for tasks/:id path: ${path}`)

        return { channel: 'Admin::Tasks::ShowChannel' }
      }

      return { channel: 'Admin::Tasks::ShowChannel', id: Number(match[1]) }
    },
    onConnect: (subscription, path) => {
      socketLog(`tasks/:id socket connected ${path} ${subscription?.identifier ?? 'no-identifier'}`)
      const match = path.match(/^\/tasks\/(\d+)$/)
      const taskId = match?.[1] ?? 'unknown'

      try {
        subscription.perform?.('join_table', { table: `tasks/${taskId}` })
      } catch (err) {
        console.error('subscription perform(join_table, tasks/:id) failed', err)
      }

      return () => {
        try {
          subscription.perform?.('leave_table', { table: `tasks/${taskId}` })
        } catch (err) {
          console.error('subscription perform(leave_table, tasks/:id) failed', err)
        }
      }
    },
  },

  {
    name: 'failed_rows',
    // Use RegExp to avoid matching e.g. `/tasks/new`
    match: [/^\/tasks\/\d+$/],
    url: WS_URL,
    subscription: (path: string) => {
      const match = path.match(/^\/tasks\/(\d+)/)
      if (!match) {
        return { channel: 'Admin::Tasks::FailedRowsChannel' }
      }

      return {
        channel: 'Admin::Tasks::FailedRowsChannel',
        task_id: Number(match[1]),
      }
    },
    onConnect: (subscription, path) => {
      socketLog(`failed_rows socket connected ${path} ${subscription?.identifier ?? 'no-identifier'}`)

      try {
        subscription.perform?.('retrieve_resources')
      } catch (err) {
        console.error('retrieve_resources failed', err)
      }

      return () => {
        try {
          subscription.perform?.('leave_table', { table: 'failed_rows' })
        } catch (err) {
          console.error('subscription perform(leave_table, failed_rows) failed', err)
        }
      }
    },
  },

  {
    name: 'scheduled_actions',
    match: ['/scheduled_actions'],
    url: WS_URL,
    subscription: { channel: 'Admin::ScheduledActions::IndexChannel' },
    onConnect: (subscription) => {
      try {
        subscription.perform?.('join_table', { table: 'scheduled_actions' })
      } catch (err) {
        console.error('subscription perform(join_table) for "scheduled_actions" failed', err)
      }

      return () => {
        try {
          subscription.perform?.('leave_table', { table: 'scheduled_actions' })
        } catch (err) {
          console.error('subscription perform(leave_table) for "scheduled_actions" failed', err)
        }
      }
    },
  },

  {
    name: 'users',
    match: ['/users'],
    url: WS_URL,
    subscription: { channel: 'Admin::Users::IndexChannel' },
    onConnect: (subscription) => {
      socketLog(`onConnect users ${subscription.identifier}`)

      try {
        subscription.perform?.('join_table', { table: 'users' })
      } catch (err) {
        console.error('subscription perform(join_table, users) failed', err)
      }

      return () => {
        try {
          subscription.perform?.('leave_table', { table: 'users' })
        } catch (err) {
          console.error('subscription perform(leave_table, users) failed', err)
        }
      }
    },
  },

  {
    name: 'users/:id',
    // Use RegExp to avoid matching e.g. `/users/new`
    match: [/^\/users\/\d+$/],
    url: WS_URL,
    subscription: (path: string) => {
      const match = path.match(/^\/users\/(\d+)$/)

      if (!match) {
        console.warn(`No match found for users/:id path: ${path}`)

        return { channel: 'Admin::Users::ShowChannel' }
      }

      return { channel: 'Admin::Users::ShowChannel', id: Number(match[1]) }
    },
    onConnect: (subscription, path) => {
      socketLog(`onConnect users/:id ${subscription.identifier}`)
      const match = path.match(/^\/users\/(\d+)$/)
      const userId = match?.[1] ?? 'unknown'

      try {
        subscription.perform?.('join_table', { table: `users/${userId}` })
      } catch (err) {
        console.error('subscription perform(join_table, users/:id) failed', err)
      }

      return () => {
        try {
          subscription.perform?.('leave_table', { table: `users/${userId}` })
        } catch (err) {
          console.error('subscription perform(leave_table, users/:id) failed', err)
        }
      }
    },
  },

  {
    name: 'payouts',
    match: ['/payouts'],
    url: WS_URL,
    subscription: { channel: 'Admin::Payouts::IndexChannel' },
    onConnect: (subscription) => {
      try {
        subscription.perform?.('join_table', { table: 'payouts' })
      } catch (err) {
        console.error('subscription perform(join_table) for "payouts" failed', err)
      }

      return () => {
        try {
          subscription.perform?.('leave_table', { table: 'payouts' })
        } catch (err) {
          console.error('subscription perform(leave_table) for "payouts" failed', err)
        }
      }
    },
  },

  {
    name: 'coupons',
    match: ['/coupons'],
    url: WS_URL,
    subscription: { channel: 'Admin::Coupons::IndexChannel' },
    onConnect: (subscription) => {
      try {
        subscription.perform?.('join_table', { table: 'coupons' })
      } catch (err) {
        console.error('subscription perform(join_table) for "coupons" failed', err)
      }

      return () => {
        try {
          subscription.perform?.('leave_table', { table: 'coupons' })
        } catch (err) {
          console.error('subscription perform(leave_table) for "coupons" failed', err)
        }
      }
    },
  },

  {
    name: 'coupons/:id',
    match: [/^\/coupons\/\d+$/],
    url: WS_URL,
    subscription: (path: string) => {
      const match = path.match(/^\/coupons\/(\d+)$/)
      if (!match) {
        console.warn(`No match found for coupons/:id path: ${path}`)

        return { channel: 'Admin::Coupons::ShowChannel' }
      }

      return { channel: 'Admin::Coupons::ShowChannel', id: Number(match[1]) }
    },
  },

  {
    name: 'discount_tiers',
    match: ['/discount_tiers'],
    url: WS_URL,
    subscription: { channel: 'Admin::DiscountTiers::IndexChannel' },
    onConnect: (subscription) => {
      try {
        subscription.perform?.('join_table', { table: 'discount_tiers' })
      } catch (err) {
        console.error('subscription perform(join_table) for "discount_tiers" failed', err)
      }

      return () => {
        try {
          subscription.perform?.('leave_table', { table: 'discount_tiers' })
        } catch (err) {
          console.error('subscription perform(leave_table) for "discount_tiers" failed', err)
        }
      }
    },
  },

  {
    name: 'tags',
    match: ['/payouts', '/users', '/accounts'],
    url: WS_URL,
    subscription: { channel: 'Admin::Tags::IndexChannel' },
    onConnect: (subscription) => {
      try {
        subscription.perform?.('join_table', { table: 'tags' })
      } catch (err) {
        console.error('subscription perform(join_table) for "tags" failed', err)
      }

      return () => {
        try {
          subscription.perform?.('leave_table', { table: 'tags' })
        } catch (err) {
          console.error('subscription perform(leave_table) for "tags" failed', err)
        }
      }
    },
  },
]
